<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const store = useStore();
const { t } = useI18n();

const [showDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const scheduledMessages = computed(() =>
  (currentChat.value.messages || [])
    .filter(message => message.status === MESSAGE_STATUS.SCHEDULED)
    .sort((a, b) => (a.scheduled_at || 0) - (b.scheduled_at || 0))
);

const scheduledCount = computed(() => scheduledMessages.value.length);

const previewContent = message =>
  message.content || t('CONVERSATION.SCHEDULED_MESSAGE.ATTACHMENT');

const scheduledTime = message =>
  messageTimestamp(message.scheduled_at, 'LLL d, h:mm a');

const cancelScheduledMessage = message => {
  store.dispatch('deleteMessage', {
    conversationId: message.conversation_id,
    messageId: message.id,
  });
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="scheduledCount"
    v-on-clickaway="() => toggleDropdown(false)"
    class="relative flex items-center group"
  >
    <NextButton
      v-tooltip="$t('CONVERSATION.SCHEDULED_MESSAGE.LIST_TITLE')"
      sm
      ghost
      slate
      icon="i-ph-clock"
      :label="`${scheduledCount}`"
      class="rounded-md group-hover:bg-n-alpha-2"
      @click="toggleDropdown()"
    />
    <div
      v-if="showDropdown"
      class="absolute z-50 mt-1 top-full ltr:right-0 rtl:left-0 w-80 max-h-96 overflow-y-auto bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container rounded-xl shadow-lg p-1"
    >
      <div class="px-2 py-1.5 text-xs font-medium text-n-slate-11">
        {{ $t('CONVERSATION.SCHEDULED_MESSAGE.LIST_TITLE') }}
      </div>
      <div
        v-for="message in scheduledMessages"
        :key="message.id"
        class="flex flex-col gap-1 px-2 py-2 rounded-md hover:bg-n-alpha-1"
      >
        <p class="text-sm text-n-slate-12 line-clamp-2 m-0">
          {{ previewContent(message) }}
        </p>
        <div class="flex items-center justify-between gap-2">
          <span class="flex items-center gap-1 text-xs text-n-slate-11">
            <Icon icon="i-ph-clock" class="size-3" />
            {{
              $t('CONVERSATION.SCHEDULED_MESSAGE.SCHEDULED_FOR', {
                time: scheduledTime(message),
              })
            }}
          </span>
          <NextButton
            xs
            ghost
            ruby
            :label="$t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL')"
            @click="cancelScheduledMessage(message)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
